import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import { fetchEvents, fetchTasks } from '../apiService';
import './Calendar.css';

function Calendar() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showEvents, setShowEvents] = useState(true);
  const [showTasks, setShowTasks] = useState(true);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadData() {
    if (!user.userId) return;
    setLoading(true);
    const [eventsResult, tasksResult] = await Promise.all([
      fetchEvents(user.userId),
      fetchTasks(user.userId),
    ]);
    if (eventsResult.success) setEvents(eventsResult.data);
    if (tasksResult.success) setTasks(tasksResult.data);
    setLoading(false);
  }

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  function prevMonth() { setCurrentDate(new Date(year, month - 1, 1)); }
  function nextMonth() { setCurrentDate(new Date(year, month + 1, 1)); }
  function goToToday() { setCurrentDate(new Date()); }
  function getDaysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
  function getFirstDayOfMonth(y, m) { return new Date(y, m, 1).getDay(); }

  function buildCalendarDays() {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const daysInPrevMonth = getDaysInMonth(year, month - 1);
    const days = [];
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ day: daysInPrevMonth - i, currentMonth: false, date: new Date(year, month - 1, daysInPrevMonth - i) });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, currentMonth: true, date: new Date(year, month, i) });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ day: i, currentMonth: false, date: new Date(year, month + 1, i) });
    }
    return days;
  }

  function getEventsForDate(date) {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => event.eventDate === dateStr);
  }

  function getTasksForDate(date) {
    const dateStr = date.toISOString().split('T')[0];
    return tasks.filter(t => t.dueDate === dateStr);
  }

  function isToday(date) {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  }

  const calendarDays = buildCalendarDays();
  const weeks = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }

  return (
    <DashboardLayout>
      <div className="calendar-page-header">
        <h1 className="page-title">Calendar</h1>
        <div className="calendar-filters">
          <label className={`calendar-filter ${showEvents ? 'active events' : ''}`}>
            <input type="checkbox" checked={showEvents} onChange={(e) => setShowEvents(e.target.checked)} />
            <span className="calendar-filter-dot events-dot"></span>
            Events
          </label>
          <label className={`calendar-filter ${showTasks ? 'active tasks' : ''}`}>
            <input type="checkbox" checked={showTasks} onChange={(e) => setShowTasks(e.target.checked)} />
            <span className="calendar-filter-dot tasks-dot"></span>
            Tasks
          </label>
        </div>
      </div>

      <div className="calendar-card">
        <div className="calendar-header">
          <h2 className="calendar-month">{monthNames[month]} {year}</h2>
          <div className="calendar-nav">
            <button className="cal-today-btn" onClick={goToToday}>Today</button>
            <button className="cal-nav-btn" onClick={prevMonth} aria-label="Previous month"><FiChevronLeft size={20} /></button>
            <button className="cal-nav-btn" onClick={nextMonth} aria-label="Next month"><FiChevronRight size={20} /></button>
          </div>
        </div>

        <div className="calendar-grid">
          <div className="calendar-day-names">
            {dayNames.map(day => (<div className="day-name" key={day}>{day}</div>))}
          </div>

          {loading ? (<p className="loading-text">Loading calendar...</p>) : (
            weeks.map((week, weekIndex) => (
              <div className="calendar-week" key={weekIndex}>
                {week.map((dayObj, dayIndex) => {
                  const dayEvents = showEvents ? getEventsForDate(dayObj.date) : [];
                  const dayTasks = showTasks ? getTasksForDate(dayObj.date) : [];
                  const items = [
                    ...dayEvents.map(e => ({ type: 'event', name: e.eventName, id: e.id, key: 'e-' + e.id })),
                    ...dayTasks.map(t => ({ type: 'task', name: t.taskName, id: t.id, completed: t.status === 'completed', key: 't-' + t.id })),
                  ];
                  return (
                    <div className={`calendar-day ${!dayObj.currentMonth ? 'other-month' : ''} ${isToday(dayObj.date) ? 'today' : ''}`} key={dayIndex}>
                      <span className={`day-number ${isToday(dayObj.date) ? 'today-number' : ''}`}>{dayObj.day}</span>
                      <div className="day-events">
                        {items.slice(0, 3).map(item => (
                          <div
                            className={`calendar-item clickable ${item.type} ${item.completed ? 'completed' : ''}`}
                            key={item.key}
                            title={`${item.name} — click to open`}
                            onClick={() => navigate(item.type === 'event' ? `/playbook?eventId=${item.id}` : '/tasks')}
                          >
                            {item.name}
                          </div>
                        ))}
                        {items.length > 3 && (
                          <div
                            className="calendar-item-more clickable"
                            title="View all"
                            onClick={() => navigate('/tasks')}
                          >
                            +{items.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default Calendar;
